import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const InteractiveMap = ({ filters, onMarkerClick, selectedIncident }) => {
  const [mapCenter, setMapCenter] = useState({ lat: 39.8283, lng: -98.5795 }); // Center of US
  const [zoomLevel, setZoomLevel] = useState(5);
  const [userLocation, setUserLocation] = useState(null);
  const [isLocating, setIsLocating] = useState(false);

  // Mock outage incidents with geographic coordinates
  const outageIncidents = [
    {
      id: 1,
      lat: 40.7128,
      lng: -74.0060,
      severity: 'critical',
      serviceType: 'internet',
      provider: 'Verizon FiOS',
      affectedUsers: 15420,
      duration: '2h 15m',
      status: 'active',
      title: 'Major Internet Outage in NYC',
      description: 'Widespread fiber network disruption affecting Manhattan and Brooklyn areas'
    },
    {
      id: 2,
      lat: 34.0522,
      lng: -118.2437,
      severity: 'major',
      serviceType: 'mobile',
      provider: 'T-Mobile',
      affectedUsers: 8930,
      duration: '45m',
      status: 'active',
      title: 'Mobile Service Disruption',
      description: 'Cell tower maintenance causing service interruptions in Los Angeles'
    },
    {
      id: 3,
      lat: 41.8781,
      lng: -87.6298,
      severity: 'minor',
      serviceType: 'streaming',
      provider: 'Netflix',
      affectedUsers: 2150,
      duration: '20m',
      status: 'active',
      title: 'Streaming Quality Issues',
      description: 'Users reporting buffering and connection timeouts in Chicago area'
    },
    {
      id: 4,
      lat: 47.6062,
      lng: -122.3321,
      severity: 'resolved',
      serviceType: 'cloud',
      provider: 'AWS',
      affectedUsers: 12500,
      duration: '1h 30m',
      status: 'resolved',
      title: 'AWS Service Restored',
      description: 'EC2 instances in us-west-2 region fully operational'
    },
    {
      id: 5,
      lat: 29.7604,
      lng: -95.3698,
      severity: 'major',
      serviceType: 'financial',
      provider: 'Chase Bank',
      affectedUsers: 5670,
      duration: '1h 5m',
      status: 'active',
      title: 'Online Banking Issues',
      description: 'Mobile app and website login problems reported in Houston'
    },
    {
      id: 6,
      lat: 33.4484,
      lng: -112.0740,
      severity: 'critical',
      serviceType: 'internet',
      provider: 'Cox Communications',
      affectedUsers: 18200,
      duration: '3h 20m',
      status: 'active',
      title: 'Cable Internet Outage',
      description: 'Infrastructure damage affecting Phoenix metropolitan area'
    }
  ];

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return '#DC2626';
      case 'major': return '#D97706';
      case 'minor': return '#0EA5E9';
      case 'resolved': return '#059669';
      default: return '#64748B';
    }
  };

  const getServiceIcon = (serviceType) => {
    switch (serviceType) {
      case 'internet': return 'Wifi';
      case 'mobile': return 'Smartphone';
      case 'streaming': return 'Play';
      case 'cloud': return 'Cloud';
      case 'financial': return 'CreditCard';
      case 'gaming': return 'Gamepad2';
      default: return 'AlertCircle';
    }
  };

  const handleGetUserLocation = () => {
    setIsLocating(true);
    if (navigator.geolocation) {
      navigator.geolocation?.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position?.coords;
          setUserLocation({ lat: latitude, lng: longitude });
          setMapCenter({ lat: latitude, lng: longitude });
          setZoomLevel(10);
          setIsLocating(false);
        },
        (error) => {
          console.error('Error getting location:', error);
          setIsLocating(false);
        }
      );
    } else {
      setIsLocating(false);
    }
  };

  const resetMapView = () => {
    setMapCenter({ lat: 39.8283, lng: -98.5795 });
    setZoomLevel(5);
  };

  const filteredIncidents = outageIncidents?.filter(incident => {
    if (filters?.serviceTypes?.length > 0 && !filters?.serviceTypes?.includes(incident?.serviceType)) {
      return false;
    }
    if (filters?.severity?.length > 0 && !filters?.severity?.includes(incident?.severity)) {
      return false;
    }
    if (filters?.status !== 'all' && incident?.status !== filters?.status) {
      return false;
    }
    return true;
  });

  return (
    <div className="relative w-full h-full bg-muted rounded-lg overflow-hidden">
      {/* Map Container */}
      <div className="w-full h-full relative">
        {/* Google Maps Iframe */}
        <iframe
          width="100%"
          height="100%"
          loading="lazy"
          title="Service Outage Geographic Map"
          referrerPolicy="no-referrer-when-downgrade"
          src={`https://www.google.com/maps?q=${mapCenter?.lat},${mapCenter?.lng}&z=${zoomLevel}&output=embed`}
          className="w-full h-full border-0"
        />

        {/* Map Overlay with Incident Markers */}
        <div className="absolute inset-0 pointer-events-none">
          {filteredIncidents?.map((incident) => {
            // Calculate marker position (simplified positioning for demo)
            const x = ((incident?.lng + 180) / 360) * 100;
            const y = ((90 - incident?.lat) / 180) * 100;
            
            return (
              <div
                key={incident?.id}
                className="absolute pointer-events-auto transform -translate-x-1/2 -translate-y-1/2 cursor-pointer"
                style={{ left: `${x}%`, top: `${y}%` }}
                onClick={() => onMarkerClick(incident)}
              >
                <div
                  className={`relative flex items-center justify-center w-8 h-8 rounded-full border-2 border-white shadow-lg transition-all duration-200 hover:scale-110 ${
                    selectedIncident?.id === incident?.id ? 'ring-2 ring-primary' : ''
                  }`}
                  style={{ backgroundColor: getSeverityColor(incident?.severity) }}
                >
                  <Icon 
                    name={getServiceIcon(incident?.serviceType)} 
                    size={16} 
                    color="white" 
                  />
                  
                  {/* Pulse animation for active incidents */}
                  {incident?.status === 'active' && (
                    <div 
                      className="absolute inset-0 rounded-full animate-ping opacity-75"
                      style={{ backgroundColor: getSeverityColor(incident?.severity) }}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Map Controls */}
        <div className="absolute top-4 right-4 flex flex-col space-y-2">
          <Button
            variant="outline"
            size="icon"
            onClick={handleGetUserLocation}
            loading={isLocating}
            className="bg-card shadow-lg"
            title="Center on my location"
          >
            <Icon name="MapPin" size={16} />
          </Button>
          
          <Button
            variant="outline"
            size="icon"
            onClick={resetMapView}
            className="bg-card shadow-lg"
            title="Reset map view"
          >
            <Icon name="RotateCcw" size={16} />
          </Button>
        </div>

        {/* Zoom Controls */}
        <div className="absolute bottom-20 right-4 flex flex-col space-y-1">
          <Button
            variant="outline"
            size="icon"
            onClick={() => setZoomLevel(Math.min(zoomLevel + 1, 18))}
            className="bg-card shadow-lg"
            title="Zoom in"
          >
            <Icon name="Plus" size={16} />
          </Button>
          
          <Button
            variant="outline"
            size="icon"
            onClick={() => setZoomLevel(Math.max(zoomLevel - 1, 1))}
            className="bg-card shadow-lg"
            title="Zoom out"
          >
            <Icon name="Minus" size={16} />
          </Button>
        </div>

        {/* User Location Marker */}
        {userLocation && (
          <div
            className="absolute pointer-events-none transform -translate-x-1/2 -translate-y-1/2"
            style={{ 
              left: `${((userLocation?.lng + 180) / 360) * 100}%`, 
              top: `${((90 - userLocation?.lat) / 180) * 100}%` 
            }}
          >
            <div className="flex items-center justify-center w-4 h-4 bg-primary rounded-full border-2 border-white shadow-lg">
              <div className="w-2 h-2 bg-white rounded-full"></div>
            </div>
          </div>
        )}

        {/* Loading Overlay */}
        {isLocating && (
          <div className="absolute inset-0 bg-background/50 flex items-center justify-center">
            <div className="bg-card p-4 rounded-lg shadow-lg flex items-center space-x-3">
              <Icon name="Loader2" size={20} className="animate-spin" />
              <span className="text-sm text-foreground">Getting your location...</span>
            </div>
          </div>
        )}
      </div>
      {/* Map Statistics */}
      <div className="absolute bottom-4 left-4 bg-card border border-border rounded-lg p-3 shadow-lg">
        <div className="text-xs text-muted-foreground">
          <div className="flex items-center space-x-4">
            <span>Showing: {filteredIncidents?.length} incidents</span>
            <span>•</span>
            <span>Zoom: {zoomLevel}x</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InteractiveMap;